// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@synd/XDL/Utilities";
import * as React from "react";
import { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import updateSearchFields from "@synd/App/Handlers/RetailerManagement/UpdateSearchFields";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import qs from "qs";
import { getRetailerManagementView } from "@synd/App/Store/Selectors/RetailerManagementSelector";
import loadRetailerManagementUsers from "@synd/App/Handlers/RetailerManagement/LoadUsers";
import { RetailUsersApiParameter } from "@synd/App/Services/RetailerManagementServices";
import CreateEditRetailUserModal from "@synd/App/Widgets/RetailerManagement/CreateEditRetailUserModal";

const mapStateToProps = (state: CustomAppState) => {
    const getUsersParameter = state.xRetailerManagement.getRetailUsersParameter;
    return {
        getUsersParameter,
        retailUsersDataView: getRetailerManagementView(state, getUsersParameter),
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadRetailerManagementUsers,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface RetailerManagementPageStyles {
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
}

const retailerManagementPageStyles = {
    wrapper: {
        css: css`
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 376px);
            grid-column-gap: 22px;
            ${xScreen("lte", "lg")} {
                grid-template-columns: minmax(0, 1fr) minmax(0, 317px);
            }

            ${xScreen("lte", "md")} {
                grid-template-columns: minmax(0, 1fr);
                grid-row-gap: 22px;
            }
        `,
    },
    leftSection: {
        css: css`
            grid-column: 2 / 3;
            grid-row: 1 / 2;
            display: flex;
            flex-direction: column;
            gap: 24px 20px;
            ${xScreen("lte", "md")} {
                grid-column: 1 / 2;
                grid-row: 2 / 3;
                flex-direction: row-reverse;
            }
            ${xScreen("lt", "sm")} {
                flex-direction: column-reverse;
            }
        `,
    },
};

const styles = retailerManagementPageStyles;

const RetailerManagementPage = ({
    getUsersParameter,
    retailUsersDataView,
    history,
    location,
    updateSearchFields,
    loadRetailerManagementUsers,
    id,
}: Props) => {
    const firstLoadRef = useRef(false);
    
    useEffect(() => {
        const pageSizeCookie = getCookie("RetailUsers-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        
        if (location.search) {
            const parsed = parseQueryString<RetailUsersApiParameter>(location.search);
            const apiParameter = { ...getUsersParameter, ...parsed };
            if (pageSize) {
                apiParameter.pageSize = pageSize;
            }
            updateSearchFields({
                ...apiParameter,
                type: "Replace",
            });
        }
        
        firstLoadRef.current = true;

        // Clear search when navigating away from the page (equivalent to componentWillUnmount)
        return () => {
            const unmountPageSizeCookie = getCookie("RetailUsers-PageSize");
            const unmountPageSize = unmountPageSizeCookie ? parseInt(unmountPageSizeCookie, 10) : undefined;
            updateSearchFields({ type: "Initialize", pageSize: unmountPageSize });
        };
    }, []);

    useEffect(() => {
        if (
            getUsersParameter !== undefined &&
            (!retailUsersDataView.value && !retailUsersDataView.isLoading)
        ) {
            loadRetailerManagementUsers(getUsersParameter);
        }
    }, [getUsersParameter, retailUsersDataView, loadRetailerManagementUsers]);

    useEffect(() => {
        if (firstLoadRef.current) {
            const queryString = qs.stringify(getUsersParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getUsersParameter]);

    return (
        <>
            <Page data-test-selector="RetailerManagementPage">
                <Zone contentId={id} zoneName="Content" />
                <StyledWrapper {...styles.wrapper}>
                    <Zone contentId={id} zoneName="Content1" />
                    <StyledWrapper {...styles.leftSection}>
                        <Zone contentId={id} zoneName="Content2" />
                        <Zone contentId={id} zoneName="Content3" />
                    </StyledWrapper>
                </StyledWrapper>
            </Page>
            <CreateEditRetailUserModal />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RetailerManagementPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RetailerManagementPage" instead of this constant.
 */
export const RetailerManagementPageContext = "RetailerManagementPage";
